<template>
  <form
    :id="`app-payment-card-wrapper-${_uid}`"
    class="app-payment-card-wrapper"
  >
    <div class="app-payment-card"></div>

    <input
      v-show="false"
      v-model="numberFormatted"
      class="number"
      type="hidden"
    />
    <input
      v-show="false"
      v-model="holderNameFormatted"
      class="holder-name"
      type="hidden"
    />
    <input
      v-show="false"
      v-model="expiryFormatted"
      class="expiry"
      type="hidden"
    />
    <input v-show="false" class="cvc" type="hidden" />
  </form>
</template>

<script>
import { accentsTidy } from '@/helpers'
import Card from 'card'
import CardValidator from 'card-validator'

export default {
  name: 'AppPaymentCard',

  data() {
    return {
      paymentCard: undefined,
    }
  },

  props: {
    number: {
      type: [Number, String],
    },

    holderName: {
      type: String,
    },

    expiry: {
      type: [String, Object],
    },

    securityCode: {
      type: [String, Number],
    },
  },

  watch: {
    number: {
      immediate: true,
      handler() {
        this.updateField('number')
      },
    },
    holderName: {
      immediate: true,
      handler() {
        this.updateField('holderName')
      },
    },
    expiry: {
      immediate: true,
      handler() {
        this.updateField('expiry')
      },
    },
  },

  computed: {
    holderNameFormatted() {
      return accentsTidy(this.holderName ?? '')
        .toUpperCase()
        .replace(/[^a-zA-Z 0-9]/gi, '')
    },

    numberFormatted() {
      return (this.number ?? '').toString().replace(/[^ 0-9]/gi, '')
    },

    expiryFormatted() {
      let expiry = this.expiry ?? ''

      if (typeof expiry === 'object')
        expiry = `${expiry?.month}/${expiry?.year}`

      return (this.expiry ?? '').replace(/[^/0-9]/gi, '')
    },

    numberValidator() {
      return this.validateNumber()
    },

    expiryValidator() {
      return this.validateExpiry()
    },
  },

  mounted() {
    this.init()
  },

  methods: {
    init() {
      this.renderCard()
    },

    async updateField(fieldName) {
      await this.$nextTick()

      if (fieldName === 'holderName') fieldName = 'name'

      const field = this.paymentCard?.[`$${fieldName}Input`]?.[0]

      if (field) {
        const keyupEvent = new KeyboardEvent('keyup')

        field.dispatchEvent(keyupEvent)
      }
    },

    validate({ field, value }) {
      if (!value) return 'NOT_COMPLETED'

      let validatorMethod = field

      switch (validatorMethod) {
        case 'expiry':
          validatorMethod = 'expirationDate'
          break

        case 'securityCode':
          validatorMethod = 'cvv'
          break
      }

      const validatorResult = CardValidator[validatorMethod](value)

      if (validatorResult.isValid === false) return 'INVALID'

      return 'VALID'
    },

    validateNumber(value = this.numberFormatted) {
      return this.validate({
        field: 'number',
        value,
      })
    },

    validateExpiry(value = this.expiryFormatted) {
      return this.validate({
        field: 'expiry',
        value,
      })
    },

    validateSecurityCode(value = this.securityCode) {
      return this.validate({
        field: 'securityCode',
        value,
      })
    },

    getExpiry(expiry = this.expiry) {
      const validatorResult = CardValidator.expirationDate(expiry)

      return {
        month: parseInt(validatorResult.month),
        year: parseInt(validatorResult.year),
      }
    },

    getMaxSecurityCodeLength(number = this.numberFormatted) {
      const validatorResult = CardValidator.number(number)

      return validatorResult?.card?.code?.size ?? 4
    },

    async renderCard() {
      const componentId = this._uid
      const wrapperSelector = `#app-payment-card-wrapper-${componentId}`

      this.paymentCard = new Card({
        container: `${wrapperSelector} > .app-payment-card`,
        form: wrapperSelector,
        width: 'auto',
        formatting: true,
        placeholders: {
          name: 'Nome impresso no cartão',
        },
        messages: {
          monthYear: 'mês/ano',
        },
        formSelectors: {
          numberInput: `${wrapperSelector} > .number`,
          expiryInput: `${wrapperSelector} > .expiry`,
          nameInput: `${wrapperSelector} > .holder-name`,
          cvcInput: `${wrapperSelector} > .cvc`,
        },
        masks: {
          cardNumber: '',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.app-payment-card {
  width: 100%;

  :deep(.jp-card-container) {
    width: auto;
    height: 30vh;
    padding: 0;

    .jp-card {
      min-width: 0;
    }
  }
}
</style>
